import React, { useState, useEffect } from 'react';
import settings from '../../core/settings';
import Tensai from './Tensai';
import { generateSeed, formatLocalDate, BACKEND_URL } from '../../core/utils';

export function useTensaiOrchestrator(lobby, auth) {
  const [dailyPlayed, setDailyPlayed] = useState(false);
  const [dailyInProgress, setDailyInProgress] = useState(false);
  const [dailyViewing, setDailyViewing] = useState(false);
  const [dailyPercentile, setDailyPercentile] = useState(null);
  const [dailyTotalPlayers, setDailyTotalPlayers] = useState(null);
  const [shouldStoreSoloStats, setShouldStoreSoloStats] = useState(true);

  const getTodayUTCString = formatLocalDate;

  const generateDailySeed = (date = new Date()) => {
    const key = formatLocalDate(date);
    let hash = 5381;
    for (let i = 0; i < key.length; i++) {
      hash = ((hash << 5) + hash) + key.charCodeAt(i);
      hash = hash >>> 0;
    }
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let out = '';
    for (let i = 0; i < 10; i++) {
      out += chars.charAt(hash % chars.length);
      hash = (hash * 1664525 + 1013904223) >>> 0;
    }
    return out;
  };

  // Check daily played status on mount
  useEffect(() => {
    try {
      const stored = settings.getString(settings.KEYS.DAILY_PLAYED) || '';
      const today = getTodayUTCString();
      if (stored === today) {
        setDailyPlayed(true);
      } else {
        setDailyPlayed(false);
      }
    } catch (e) {}
  }, [getTodayUTCString]);

  // Submit stored daily result when auth becomes available
  useEffect(() => {
    const submitStoredDailyIfPresent = async () => {
      if (!auth.authToken) return;
      try {
        const raw = settings.getString(settings.KEYS.DAILY_RESULT) || '';
        if (!raw) return;
        const existing = JSON.parse(raw);
        if (!existing || !existing.date) return;
        const today = getTodayUTCString();
        if (existing.date !== today) return;

        const resp = await fetch(`${BACKEND_URL}/api/daily/results`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.authToken}`,
          },
          body: JSON.stringify({ date: existing.date, score: existing.score })
        });
        const data = await resp.json();
        if (data && data.success) {
          if (!data.already_submitted) {
            setDailyPercentile(data.percentile);
            setDailyTotalPlayers(data.total_players);
          } else {
            try {
              const r = await fetch(`${BACKEND_URL}/api/daily/ranking/${existing.date}`, {
                headers: { 'Authorization': `Bearer ${auth.authToken}` }
              });
              const rd = await r.json();
              if (rd && rd.success && rd.has_ranking) {
                setDailyPercentile(rd.percentile);
                setDailyTotalPlayers(rd.total_players);
              }
            } catch (e) {}
          }
        }
      } catch (e) {}
    };

    submitStoredDailyIfPresent();
  }, [auth.authToken, getTodayUTCString]);

  const handlePlaySolo = () => {
    lobby.setGameMode('solo');
    lobby.setGameState('playing');
    lobby.setGameSeed(generateSeed());
    setDailyViewing(false);
    setShouldStoreSoloStats(true);
    lobby.setGameKey(prev => prev + 1);
  };

  const handlePlayZen = () => {
    lobby.setGameMode('zen');
    lobby.setGameState('playing');
    lobby.setGameSeed(generateSeed());
    setDailyViewing(false);
    lobby.setGameKey(prev => prev + 1);
  };

  const handleDailyChallenge = () => {
    try {
      const today = getTodayUTCString();
      const stored = settings.getString(settings.KEYS.DAILY_PLAYED) || '';
      if (stored !== today) {
        settings.set(settings.KEYS.DAILY_PLAYED, today);
        setDailyPlayed(true);
        setDailyInProgress(true);
      }
      setDailyViewing(true);
    } catch (e) {}
    setShouldStoreSoloStats(true);
    lobby.setGameMode('solo');
    lobby.setCountdownValue(3);
    lobby.setGameSeed(generateDailySeed());
    lobby.setGameState('countdown');
  };

  const handleSoloGameEnd = async (result) => {
    try {
      if (auth.authToken && shouldStoreSoloStats && result && result.score !== undefined) {
        const payload = { score: result.score };
        await fetch(`${BACKEND_URL}/api/solo/results`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.authToken}`,
          },
          body: JSON.stringify(payload),
        });
      }
    } catch (e) {}

    try {
      if (dailyInProgress && result && result.score !== undefined) {
        const today = getTodayUTCString();
        try {
          const existingRaw = settings.getString(settings.KEYS.DAILY_RESULT) || '';
          if (existingRaw) {
            const existing = JSON.parse(existingRaw);
            if (existing && existing.date === today) {
              setDailyInProgress(false);
              return;
            }
          }
        } catch (e) {}

        const payload = {
          date: today,
          score: result.score,
          seed: result.seed || lobby.gameSeed,
          grid: result.grid || null,
        };
        settings.set(settings.KEYS.DAILY_RESULT, JSON.stringify(payload));
        setDailyInProgress(false);

        if (auth.authToken) {
          try {
            console.log('[Daily] Submitting score to server:', { date: today, score: result.score });
            const response = await fetch(`${BACKEND_URL}/api/daily/results`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.authToken}`,
              },
              body: JSON.stringify({ date: today, score: result.score }),
            });
            const data = await response.json();
            console.log('[Daily] Server response:', data);
            if (data.success && !data.already_submitted) {
              setDailyPercentile(data.percentile);
              setDailyTotalPlayers(data.total_players);
            }
          } catch (e) {
            console.error('[Daily] API error:', e);
          }
        } else {
          console.log('[Daily] No auth token, skipping server submission');
        }
      }
    } catch (e) {}
  };

  const handlePlayWithFriends = () => {
    lobby.setGameMode('multiplayer');
    lobby.setGameState('home');
  };

  const handleBackToMenu = () => {
    lobby.resetToMenu();
    setDailyViewing(false);
  };

  const handleNewGame = (seed) => {
    const hasCustomSeed = !!(seed && typeof seed === 'string' && seed.trim());
    const finalSeed = hasCustomSeed ? seed.trim() : generateSeed();
    lobby.setGameSeed(finalSeed);
    setDailyViewing(false);
    lobby.setGameKey(prev => prev + 1);
    setShouldStoreSoloStats(!hasCustomSeed);
  };

  const handleMultiplayerScore = (score) => {
    lobby.handleMultiplayerScore(score);
  };

  const renderBlurredZenBoard = () => (
    <div className="blurred-board">
      <Tensai
        rows={10}
        cols={17}
        seed={'PLACEHLDER'}
        zenMode={true}
        onScore={() => {}}
        timerSeconds={120}
        autoStart={true}
      />
    </div>
  );

  const renderCountdownOverlay = () => (
    <div className="lobby-stage">
      {renderBlurredZenBoard()}
      <div className="countdown-overlay lobby-overlay">
        <div className="countdown-circle">
          <div className="countdown-number">{lobby.countdownValue}</div>
        </div>
        <p className="countdown-text">Get Ready!</p>
      </div>
    </div>
  );

  const showBlurredHome = (lobby.gameMode === '' || lobby.gameMode === 'multiplayer') && lobby.gameState === 'home';

  const renderBoard = () => (
    <>
      {showBlurredHome && renderBlurredZenBoard()}

      {lobby.gameMode === 'solo' && lobby.gameState === 'countdown' && renderCountdownOverlay()}

      {lobby.gameMode === 'solo' && lobby.gameState === 'playing' && (
        <Tensai
          key={lobby.gameKey}
          rows={10}
          cols={17}
          seed={lobby.gameSeed}
          zenMode={false}
          onScore={() => {}}
          onGameEnd={handleSoloGameEnd}
          timerSeconds={120}
          autoStart={true}
        />
      )}

      {lobby.gameMode === 'zen' && lobby.gameState === 'playing' && (
        <Tensai
          key={`zen-${lobby.gameKey}`}
          rows={10}
          cols={17}
          seed={lobby.gameSeed}
          zenMode={true}
          onScore={() => {}}
          timerSeconds={120}
          autoStart={true}
        />
      )}

      {lobby.gameMode === 'multiplayer' && (
        <>
          {lobby.gameState === 'lobby' && renderBlurredZenBoard()}
          {lobby.gameState === 'countdown' && renderCountdownOverlay()}
          {(lobby.gameState === 'playing' || lobby.gameState === 'finished') && (
            <Tensai
              key={`multiplayer-${lobby.gameKey}`}
              rows={10}
              cols={17}
              seed={lobby.gameSeed}
              zenMode={false}
              onScore={handleMultiplayerScore}
              timerSeconds={120}
              autoStart={true}
            />
          )}
        </>
      )}
    </>
  );

  const menuProps = {
    onPlaySolo: handlePlaySolo,
    onPlayZen: handlePlayZen,
    onDailyChallenge: handleDailyChallenge,
    dailyPlayed,
    dailyViewing,
    dailyPercentile,
    dailyTotalPlayers,
    onReviewDaily: (seed) => {
      setDailyViewing(true);
      lobby.setGameMode('solo');
      lobby.setGameState('playing');
      lobby.setGameSeed(seed);
      lobby.setGameKey(prev => prev + 1);
    },
    onPlayWithFriends: handlePlayWithFriends,
    onBackToMainMenu: handleBackToMenu,
    onNewGame: handleNewGame,
    onRestartGame: lobby.restartGame,
    onStartGame: lobby.startGame,
  };

  return { menuProps, renderBoard };
}
